import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Button
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { apiClient } from '../../lib/apiClient';
import { useNavigate } from 'react-router-dom';

function ChurchesList({ user }) {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChurches = async () => {
      try {
        const data = await apiClient.getAllChurches();
        setChurches(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchChurches();
    }
  }, [user]);

  const handleDeactivate = async (churchId) => {
    if (!window.confirm('Are you sure you want to deactivate this church?')) {
      return;
    }

    try {
      await apiClient.deactivateChurch(churchId, user.id);
      setChurches(churches.map(c => c.id === churchId ? { ...c, isActive: false } : c));
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 2 
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Churches
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/super-admin/create-church')}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Create Church
        </Button>
      </Box>
      <Box sx={{ overflowX: 'auto', mt: 3 }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontWeight: 'bold' }}>City</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontWeight: 'bold' }}>State</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Admins</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {churches.map((church) => (
                <TableRow key={church.id}>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{church.name}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{church.address?.city || '-'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{church.address?.state || '-'}</TableCell>
                  <TableCell>{church.churchAdminIds?.length || 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={church.isActive ? 'Active' : 'Inactive'}
                      color={church.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/super-admin/churches/${church.id}`)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    {church.isActive && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeactivate(church.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}

export default ChurchesList;

